/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, ChangeEvent } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import axiosInstance from "helpers/api";
import toast from "react-hot-toast";
import { ColumnDef } from "@tanstack/react-table";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import CustomLoader from "components/common/CustomLoader";
import NoDataFound from "components/common/NoDataFound";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import Button from "components/base/Button";
import Badge from "components/base/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlus, 
  faFilter, 
  faBolt, 
  faUsers, 
  faBrain,
  faRobot,
  faPhone,
  faTag,
  faSearch
} from "@fortawesome/free-solid-svg-icons";
import { leadBreadcrumbItems } from "data/commonData";
import AddLeadModal from "components/modules/crm/deal/AddLeadModal";
import LeadsFilterModal from "components/modals/LeadsFilterModal";
import { debounce } from "lodash";
import "./LeadsResponsive.css";

// Enhanced lead interface matching actual database schema
interface Lead {
  id: string;
  lead_first_name: string;
  lead_last_name?: string;
  lead_phone?: string;
  lead_email?: string;
  lead_source?: string;
  lead_types_id?: number; // Correct foreign key name
  lead_type?: {
    id: number;
    lead_type: string;
  }; // This comes from the LeadTypes JOIN
  lead_type_status_id?: number;
  lead_status?: {
    id: number;
    status: string;
  }; // This comes from the LeadStatus JOIN
  lead_tags?: any; // JSON field in database
  lead_score?: number;
  priority_level?: 'low' | 'medium' | 'high' | 'urgent';
  creator_id: string;
  deal_id?: string;
  created_on: string;
  updated_on?: string;
}

// AI-enhanced columns with glassmorphism styling
const leadsColumns: ColumnDef<Lead>[] = [
  {
    accessorKey: "lead_first_name",
    header: "Lead Information",
    cell: ({ row: { original } }) => {
      const firstName = original.lead_first_name || 'Unknown';
      const lastName = original.lead_last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      return (
        <div 
          className="d-flex align-items-center"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '12px',
            border: '1px solid rgba(0, 212, 255, 0.1)'
          }}
        >
          {/* AI Avatar */}
          <div 
            className="me-3"
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
              position: 'relative'
            }}
          >
            <div 
              style={{ 
                color: '#ffffff', 
                fontWeight: 600, 
                fontSize: '16px',
                textShadow: '0 0 10px rgba(255, 255, 255, 0.5)'
              }}
            >
              {firstName[0]?.toUpperCase() || 'U'}
            </div>
          </div>
          
          <div style={{ flex: 1 }}>
            <Link 
              to={`/leads/${original.id}?type=activity`} 
              className="d-block fw-semibold text-decoration-none"
              style={{
                color: '#1f2937',
                fontSize: '15px',
                textShadow: '0 0 10px rgba(0, 212, 255, 0.2)'
              }}
            >
              {fullName}
            </Link>
            
            {original.lead_email && (
              <div 
                className="text-muted"
                style={{ fontSize: '12px', marginTop: '2px' }}
              >
                {original.lead_email}
              </div>
            )}
          </div>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: "35%" } },
      cellProps: { className: "py-2" }
    }
  },
  {
    accessorKey: "lead_phone",
    header: "Contact",
    cell: ({ row: { original } }) => (
      <div>
        {original.lead_phone ? (
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faPhone} 
              className="me-2 text-primary" 
              style={{ fontSize: '12px' }}
            />
            <span style={{ fontSize: '13px', fontWeight: 500 }}>
              {original.lead_phone}
            </span>
          </div>
        ) : (
          <span className="text-muted" style={{ fontSize: '12px' }}>No phone</span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { width: "20%" } },
      cellProps: { className: "py-2" }
    }
  },
  {
    accessorKey: "lead_source",
    header: "Type & Source",
    cell: ({ row: { original } }) => {
      const getTagColor = (tag: string) => {
        const tagLower = tag.toLowerCase();
        if (tagLower.includes('hot') || tagLower.includes('urgent') || tagLower.includes('priority')) {
          return { bg: '#ef4444', color: '#ffffff' }; // Red for hot/urgent
        } else if (tagLower.includes('warm') || tagLower.includes('interested')) {
          return { bg: '#f59e0b', color: '#ffffff' }; // Orange for warm
        } else if (tagLower.includes('cold') || tagLower.includes('prospect')) {
          return { bg: '#6b7280', color: '#ffffff' }; // Gray for cold/prospect
        } else if (tagLower.includes('website') || tagLower.includes('online')) {
          return { bg: '#3b82f6', color: '#ffffff' }; // Blue for web sources
        } else if (tagLower.includes('referral') || tagLower.includes('sphere')) {
          return { bg: '#10b981', color: '#ffffff' }; // Green for referrals
        } else if (tagLower.includes('paid') || tagLower.includes('ad')) {
          return { bg: '#8b5cf6', color: '#ffffff' }; // Purple for paid ads
        } else {
          return { bg: '#64748b', color: '#ffffff' }; // Default gray
        }
      };

      // Helper function to get lead type color
      const getLeadTypeColor = (leadType: string) => {
        const typeLower = leadType.toLowerCase();
        if (typeLower.includes('buyer')) {
          return { bg: '#10b981', color: '#ffffff' }; // Green for buyers
        } else if (typeLower.includes('seller')) {
          return { bg: '#3b82f6', color: '#ffffff' }; // Blue for sellers
        } else if (typeLower.includes('investor')) {
          return { bg: '#8b5cf6', color: '#ffffff' }; // Purple for investors
        } else {
          return { bg: '#6b7280', color: '#ffffff' }; // Default gray
        }
      };

      // Helper function to get lead status color
      const getLeadStatusColor = (leadStatus: string) => {
        const statusLower = leadStatus.toLowerCase();
        if (statusLower.includes('under contract') || statusLower.includes('contract')) {
          return { bg: '#f59e0b', color: '#ffffff' }; // Orange for under contract
        } else if (statusLower.includes('active') || statusLower.includes('looking')) {
          return { bg: '#22c55e', color: '#ffffff' }; // Green for active
        } else if (statusLower.includes('closed') || statusLower.includes('completed')) {
          return { bg: '#ef4444', color: '#ffffff' }; // Red for closed
        } else if (statusLower.includes('pending') || statusLower.includes('processing')) {
          return { bg: '#8b5cf6', color: '#ffffff' }; // Purple for pending
        } else {
          return { bg: '#6b7280', color: '#ffffff' }; // Default gray
        }
      };

      // Format source name for better display
      const formatSourceName = (source: string) => {
        if (!source) return '';
        return source
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase())
          .trim();
      };

      return (
        <div style={{ maxWidth: '140px' }}>
          {/* Lead Type Badge - From LeadTypes table */}
          {original.lead_type?.lead_type && (
            <Badge 
              className="mb-1 d-block"
              style={{ 
                fontSize: '12px',
                padding: '3px 8px',
                borderRadius: '12px',
                background: getLeadTypeColor(original.lead_type.lead_type).bg,
                color: getLeadTypeColor(original.lead_type.lead_type).color,
                border: 'none',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={`Lead Type: ${original.lead_type.lead_type}`}
            >
              {original.lead_type.lead_type}
            </Badge>
          )}

          {/* Lead Status Badge - From LeadStatus table */}
          {original.lead_status?.status && (
            <Badge 
              className="mb-1 d-block"
              style={{ 
                fontSize: '12px',
                padding: '3px 8px',
                borderRadius: '12px',
                background: getLeadStatusColor(original.lead_status.status).bg,
                color: getLeadStatusColor(original.lead_status.status).color,
                border: 'none',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={`Lead Status: ${original.lead_status.status}`}
            >
              {original.lead_status.status}
            </Badge>
          )}
          
          {/* Source Badge */}
          {original.lead_source && (
            <Badge 
              bg="secondary" 
              className="mb-1 d-block"
              style={{ 
                fontSize: '12px',
                padding: '2px 6px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                border: 'none',
                fontWeight: 500,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                opacity: 0.9
              }}
              title={`Source: ${formatSourceName(original.lead_source)}`}
            >
              {formatSourceName(original.lead_source)}
            </Badge>
          )}
          
          {/* Tags - Only show if they exist */}
          {Array.isArray(original.lead_tags) && original.lead_tags.length > 0 && (
            <div className="d-flex flex-wrap gap-1 mt-1">
              {original.lead_tags.map((tag: string, index: number) => {
                const tagStyle = getTagColor(tag);
                return (
                  <Badge 
                    key={index}
                    style={{ 
                      fontSize: '6px',
                      padding: '1px 4px',
                      borderRadius: '6px',
                      background: tagStyle.bg,
                      color: tagStyle.color,
                      fontWeight: 500,
                      border: 'none',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      maxWidth: '50px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={`Tag: ${tag}`}
                  >
                    {index === 0 && <FontAwesomeIcon icon={faTag} className="me-1" style={{ fontSize: '5px' }} />}
                    {tag.replace(/_/g, ' ')}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: "20%" } },
      cellProps: { className: "py-2" }
    }
  },
  {
    accessorKey: "created_on",
    header: "Created",
    cell: ({ row: { original } }) => {
      const date = new Date(original.created_on);
      const formattedDate = date.toLocaleDateString();
      const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      return (
        <div>
          <div style={{ color: '#1f2937', fontSize: '13px', fontWeight: 500 }}>
            {formattedDate}
          </div>
          <div style={{ color: '#64748b', fontSize: '11px' }}>
            {formattedTime}
          </div>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: "15%" } },
      cellProps: { className: "py-2" }
    }
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row: { original } }) => (
      <Link 
        to={`/leads/${original.id}?type=activity`} 
        className="btn btn-sm"
        style={{
          background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
          border: 'none',
          color: '#ffffff',
          borderRadius: '8px',
          fontSize: '12px',
          padding: '6px 16px',
          fontWeight: 500,
          boxShadow: '0 2px 8px rgba(0, 212, 255, 0.3)'
        }}
      >
        View
      </Link>
    ),
    meta: {
      headerProps: { style: { width: "10%" } },
      cellProps: { className: "py-2" }
    }
  }
];

const LeadsAI = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [status, setStatus] = useState("idle");
  const [searchInput, setSearchInput] = useState("");
  const [openAddLeadModal, setOpenAddLeadModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);

  // Create table with AI styling
  const table = useAdvanceTable({
    data: leads,
    columns: leadsColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true
  });

  // Calculate AI metrics using actual database fields
  const leadMetrics = {
    total: leads.length,
    highPriority: leads.filter(lead => lead.priority_level === 'high' || lead.priority_level === 'urgent').length,
    highScore: leads.filter(lead => (lead.lead_score || 0) >= 75).length,
    conversionRate: leads.length > 0 ? Math.round((leads.filter(lead => (lead.lead_score || 0) >= 80).length / leads.length) * 100) : 0
  };

  // Enhanced fetch function with better error handling and search support
  const fetchLeads = async (searchValue = "") => {
    try {
      setStatus("loading");
      setAiProcessing(true);
      
      // Note: Backend should include LeadTypes join to get lead_type names
      // SELECT leads.*, leadtypes.name as lead_type FROM leads LEFT JOIN leadtypes ON leads.lead_types_id = leadtypes.id
      const response = await axiosInstance.get("/dashboard/leads", {
        params: searchValue ? { search: searchValue } : {}
      });
      const leadsData = response.data.leads || response.data.data || response.data || [];
      
      // Transform data to handle missing fields gracefully and map LeadType foreign key
      const transformedLeads = leadsData.map((lead: any) => {        
        // Parse JSON tags to handle multiple tags properly - NO MOCK DATA
        let parsedTags = [];
        if (lead.lead_tags) {
          try {
            if (typeof lead.lead_tags === 'string') {
              // Try to parse as JSON first
              try {
                parsedTags = JSON.parse(lead.lead_tags);
              } catch {
                // If not JSON, treat as single tag
                parsedTags = [lead.lead_tags];
              }
            } else if (Array.isArray(lead.lead_tags)) {
              parsedTags = lead.lead_tags;
            } else {
              parsedTags = [String(lead.lead_tags)];
            }
            
            // Ensure it's always an array and filter out empty values
            if (!Array.isArray(parsedTags)) {
              parsedTags = [String(parsedTags)];
            }
            parsedTags = parsedTags.filter(tag => tag && tag.trim()).slice(0, 3); // Limit to 3 tags max
            
          } catch (e) {
            console.log('Error parsing tags:', e);
            parsedTags = [];
          }
        }

        console.log("🔍 Processing Lead:", {
          id: lead.id,
          first_name: lead.lead_first_name,
          last_name: lead.lead_last_name,
          phone: lead.lead_phone,
          email: lead.lead_email,
          source: lead.lead_source,
          types_id: lead.lead_types_id,
          lead_type_from_db: lead.lead_type?.lead_type || null,
          lead_type_id_from_db: lead.lead_type?.id || null,
          lead_status_from_db: lead.lead_status?.status || null,
          lead_status_id_from_db: lead.lead_status?.id || null,
          tags_raw: lead.lead_tags,
          tags_parsed: parsedTags,
          score: lead.lead_score,
          priority: lead.priority_level,
          using_real_type_data: lead.lead_type?.lead_type ? '✅ YES' : '❌ NO',
          using_real_status_data: lead.lead_status?.status ? '✅ YES' : '❌ NO',
          has_tags: lead.lead_tags ? '✅ YES' : '❌ NO'
        });
        
        return {
          ...lead,
          lead_last_name: lead.lead_last_name || '',
          lead_phone: lead.lead_phone || '',
          lead_email: lead.lead_email || '',
          lead_source: lead.lead_source || 'Unknown',
          lead_types_id: lead.lead_types_id || null,
          lead_type: lead.lead_type || null, // Keep full object from LeadTypes join
          lead_status: lead.lead_status || null, // Keep full object from LeadStatus join
          lead_tags: parsedTags,
          lead_score: lead.lead_score || 50.0,
          priority_level: lead.priority_level || 'medium'
        };
      });
      
      setLeads(transformedLeads);
    } catch (error: any) {
      console.error("❌ Fetch Error:", error);
      toast.error("Failed to fetch leads");
      setLeads([]);
    } finally {
      setStatus("idle");
      setAiProcessing(false);
    }
  };

  // Debounced search function like in Deals.tsx
  const debouncedFetchLeadData = debounce(async (searchValue) => {
    await fetchLeads(searchValue);
  }, 500);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (searchInput !== undefined) {
      setStatus("loading");
      debouncedFetchLeadData(searchInput);
    }
    return () => {
      setStatus("idle");
      debouncedFetchLeadData.cancel();
    };
  }, [searchInput]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        position: 'relative'
      }}
      className="leads-container"
    >
      <Helmet>
        <title>AI Lead Management | KairosIQ</title>
      </Helmet>

      {/* AI Intelligence Header */}
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(15px)',
          margin: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="container-fluid">
          <PageBreadcrumb items={leadBreadcrumbItems} />

          <div className="d-flex align-items-center justify-content-between mb-4" style={{ marginTop: '16px' }}>
            <div className="d-flex align-items-center">
              <div style={{ position: 'relative', marginRight: '16px' }}>
                <FontAwesomeIcon 
                  icon={faBolt} 
                  style={{ 
                    color: '#00d4ff', 
                    fontSize: '32px',
                    filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))',
                    animation: aiProcessing ? 'pulse 2s infinite' : 'none'
                  }} 
                />
              </div>
              <div className="lead-management-center">
                <h1 style={{ 
                  color: '#1f2937', 
                  fontWeight: 600, 
                  marginBottom: '4px',
                  textShadow: '0 2px 10px rgba(0, 212, 255, 0.2)',
                  fontSize: '2.2rem'
                }}>
                  AI Lead Management Center
                </h1>
                <p className="mb-0" style={{ 
                  fontSize: '16px',
                  color: '#64748b'
                }}>
                  Neural network-powered lead intelligence with advanced pipeline analytics
                </p>
              </div>
            </div>
            
            {/* AI Status Indicator */}
            <div 
              className="d-flex align-items-center"
              style={{
                background: aiProcessing 
                  ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                padding: '8px 16px',
                borderRadius: '20px',
                border: aiProcessing 
                  ? '1px solid rgba(245, 158, 11, 0.3)'
                  : '1px solid rgba(16, 185, 129, 0.3)'
              }}
            >
              <div 
                className="rounded-circle me-2"
                style={{
                  width: '8px',
                  height: '8px',
                  background: aiProcessing ? '#f59e0b' : '#10b981',
                  animation: 'pulse 2s infinite'
                }}
              />
              <span 
                className="fw-semibold"
                style={{ 
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: aiProcessing ? '#f59e0b' : '#10b981'
                }}
              >
                {aiProcessing ? 'AI PROCESSING' : 'AI ACTIVE'}
              </span>
            </div>
          </div>

          {/* AI Metrics Grid */}
          <div 
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}
            className="ai-metrics"
          >
            {/* Total Leads */}
            <div 
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(0, 212, 255, 0.2)',
                borderRadius: '16px',
                padding: '20px',
                transition: 'all 0.3s ease'
              }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    Total Leads
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1f2937', textShadow: '0 2px 10px rgba(0, 212, 255, 0.2)', marginBottom: '8px' }}>
                    {leadMetrics.total}
                  </div>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faUsers} style={{ color: '#00d4ff', marginRight: '8px', fontSize: '12px' }} />
                    <span style={{ color: '#64748b', fontSize: '12px' }}>Active pipeline</span>
                  </div>
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)' }}>
                  <FontAwesomeIcon icon={faUsers} style={{ color: '#ffffff', fontSize: '20px' }} />
                </div>
              </div>
            </div>

            {/* AI Conversion Score */}
            <div style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)', backdropFilter: 'blur(15px)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '16px', padding: '20px', transition: 'all 0.3s ease' }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>AI Conversion Score</div>
                  <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1f2937', textShadow: '0 2px 10px rgba(245, 158, 11, 0.2)', marginBottom: '8px' }}>{leadMetrics.conversionRate}%</div>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faBrain} style={{ color: '#f59e0b', marginRight: '8px', fontSize: '12px' }} />
                    <span style={{ color: '#64748b', fontSize: '12px' }}>Neural analysis</span>
                  </div>
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 20px rgba(245, 158, 11, 0.3)' }}>
                  <FontAwesomeIcon icon={faRobot} style={{ color: '#ffffff', fontSize: '20px' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="primary"
              startIcon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setOpenAddLeadModal(true)}
              style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                fontWeight: 600,
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
              }}
            >
              Add New Lead
            </Button>
            
            <div className="d-flex gap-2">
              <SearchBox
                onChange={handleSearchInputChange}
                placeholder="Search leads by name, email, phone..."
                className="me-2"
                prepend={<FontAwesomeIcon icon={faSearch} style={{ color: '#64748b' }} />}
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Button
                variant="outline-primary"
                onClick={() => setOpenFilterModal(true)}
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(0, 212, 255, 0.4)',
                  color: '#1f2937',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontWeight: 600
                }}
              >
                <FontAwesomeIcon icon={faFilter} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* AI-Enhanced Table Container */}
      <div 
        style={{
          margin: '24px',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
        className="leads-table-container"
      >


        <AdvanceTableProvider {...table}>
          {status === "loading" && (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <CustomLoader />
              <div className="mt-3" style={{ color: '#64748b', fontSize: '14px' }}>
                Neural network analyzing leads...
              </div>
            </div>
          )}
          
          {status === "idle" && (
            <div style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              <AdvanceTable
                tableProps={{
                  className: "phoenix-table fs-9",
                  style: {
                    background: 'transparent',
                    border: 'none'
                  }
                }}
                rowClassName="hover-actions-trigger"
              />
              <AdvanceTableFooter pagination />
            </div>
          )}
          
          {status === "idle" && leads.length === 0 && (
            <div className="p-5 text-center">
              <NoDataFound
                type="Leads"
                title="No leads found"
                description="Start building your AI-powered sales pipeline by adding your first lead."
              />
            </div>
          )}
        </AdvanceTableProvider>
      </div>

      {/* Modals */}
      <AddLeadModal
        show={openAddLeadModal}
        handleClose={() => setOpenAddLeadModal(false)}
        setLeads={setLeads}
        leads={leads}
      />

      <LeadsFilterModal
        show={openFilterModal}
        handleClose={() => setOpenFilterModal(false)}
        setLeads={setLeads}
      />

      {/* AI-Forward CSS Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          .hover-actions-trigger:hover {
            background: rgba(0, 212, 255, 0.05) !important;
            transform: translateY(-1px);
            transition: all 0.2s ease;
          }
          
          .intelligence-header:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
            transition: all 0.3s ease;
          }

          /* Improved Checkbox Alignment */
          .phoenix-table input[type="checkbox"] {
            width: 16px !important;
            height: 16px !important;
            margin: 0 !important;
            vertical-align: middle !important;
            accent-color: #3b82f6 !important;
            border-radius: 3px !important;
          }

          .phoenix-table th:first-child,
          .phoenix-table td:first-child {
            width: 50px !important;
            text-align: center !important;
            vertical-align: middle !important;
            padding: 12px 8px !important;
          }

          .phoenix-table th:first-child input[type="checkbox"],
          .phoenix-table td:first-child input[type="checkbox"] {
            display: block !important;
            margin: 4px 0 0 0 !important;
          }

          /* Better row spacing and alignment */
          .phoenix-table tbody tr {
            border-bottom: 1px solid rgba(229, 231, 235, 0.6) !important;
          }

          .phoenix-table tbody tr:hover {
            background: rgba(59, 130, 246, 0.04) !important;
          }

          .phoenix-table thead th {
            background: rgba(249, 250, 251, 0.8) !important;
            border-bottom: 2px solid rgba(229, 231, 235, 0.8) !important;
            font-weight: 600 !important;
            color: #374151 !important;
            padding: 16px 12px !important;
            font-size: 13px !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            text-align: left !important;
            white-space: nowrap !important;
          }

          .phoenix-table tbody td {
            padding: 16px 12px !important;
            vertical-align: middle !important;
            text-align: left !important;
          }
          
          /* Fix for specific column alignment */
          .phoenix-table th[data-column-index="0"],
          .phoenix-table td[data-column-index="0"] {
            text-align: left !important;
            padding-left: 16px !important;
          }
          
          /* Ensure consistent column widths */
          .phoenix-table table {
            table-layout: fixed !important;
            width: 100% !important;
          }
        `
      }} />
    </div>
  );
};

export default LeadsAI; 